import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { ChevronDown } from '@untitledui/icons';
import { Button } from '@ui/components/base/buttons/button';
import { cx } from '@ui/utils/cx';
import glyph from '../../../packages/assets/src/brand/glyph-consumer.svg';
import wordmark from '../../../packages/assets/src/brand/wordmark-long-dark.svg';
import { currentRole, signOut } from './auth';
import { canAccess, type AdminCapability } from './auth-policy';

const groups = [
  {
    label: 'Overview',
    routes: [{ to: '/', label: 'Operations overview', end: true, capability: 'admin:workspace' }],
  },
  {
    label: 'People & access',
    routes: [
      { to: '/waitlist', label: 'Waitlist', capability: 'admin:workspace' },
      { to: '/accounts', label: 'Accounts', capability: 'admin:workspace' },
      { to: '/consumers', label: 'Consumers', capability: 'admin:workspace' },
      { to: '/stylists', label: 'Stylists', capability: 'admin:workspace' },
      {
        to: '/stylist-assignments',
        label: 'Stylist assignments',
        capability: 'admin:workspace',
      },
      { to: '/account-deletions', label: 'Account deletions', capability: 'admin:workspace' },
    ],
  },
  {
    label: 'Wardrobe & styling',
    routes: [
      // A check after the fact, not a publish gate: members already have
      // every garment in it. Workflow jobs is the machine queue behind it.
      {
        to: '/garment-quality-check',
        label: 'Garment quality check',
        capability: 'admin:workspace',
      },
      {
        to: '/enrichment-failures',
        label: 'Enrichment failures',
        capability: 'admin:workspace',
      },
      // One entry, not three, and named for the one table it reads. Looks &
      // recommendations, Profiles & body data and Curated boxes were the same
      // component reading five tables, four of which nothing writes; "Member
      // product records" was then a label for that arbitrary grouping rather
      // than for anything a reader could ask about.
    ],
  },
  {
    label: 'Operations',
    routes: [
      { to: '/workflow-jobs', label: 'Workflow jobs', capability: 'admin:workspace' },
      {
        to: '/operator-controls',
        label: 'Operator controls',
        capability: 'admin:privileged-operator',
      },
      {
        to: '/email-deliveries',
        label: 'Email deliveries',
        capability: 'admin:privileged-operator',
      },
      { to: '/push-delivery', label: 'Push delivery', capability: 'admin:privileged-operator' },
      { to: '/ai-costs', label: 'AI costs', capability: 'admin:workspace' },
      // A product concern, not infrastructure: which release of which app is
      // crashing on members' devices. Infrastructure lives in the console.
      { to: '/mobile-errors', label: 'Mobile error reports', capability: 'admin:workspace' },
    ],
  },
  {
    label: 'Measurement',
    routes: [
      { to: '/product-metrics', label: 'Product metrics', capability: 'admin:workspace' },
      { to: '/garment-data', label: 'Garment data', capability: 'admin:workspace' },
      { to: '/searches', label: 'Searches', capability: 'admin:workspace' },
      { to: '/feedback', label: 'Feedback', capability: 'admin:workspace' },
      { to: '/audit-log', label: 'Audit log', capability: 'admin:workspace' },
      { to: '/activity-logs', label: 'Event log', capability: 'admin:workspace' },
      // Response times is routed but deliberately absent here: both endpoints
      // it reads are unimplemented, so a nav entry would promise a screen
      // that cannot answer anything.
    ],
  },
  {
    label: 'Configuration',
    routes: [
      {
        to: '/usage-limits',
        label: 'Usage limit defaults',
        capability: 'admin:privileged-operator',
      },
      {
        to: '/feature-flags',
        label: 'Feature flags',
        // Read-only for managers: the list needs operations:read, a change
        // configuration:manage.
        capability: 'admin:workspace',
      },
    ],
  },
  {
    label: 'Account',
    routes: [{ to: '/change-password', label: 'Change password', capability: 'account:password' }],
  },
] as const;

export function Shell() {
  const navigate = useNavigate();
  const location = useLocation();
  const role = currentRole();
  const visibleGroups = groups
    .map((group) => ({
      ...group,
      routes: group.routes.filter((route) => canAccess(role, route.capability)),
    }))
    .filter((group) => group.routes.length > 0);
  const activeGroup = visibleGroups.find((group) =>
    group.routes.some((route) =>
      route.to === '/' ? location.pathname === '/' : location.pathname.startsWith(route.to),
    ),
  )?.label;
  const [expandedGroup, setExpandedGroup] = useState<string>(activeGroup ?? 'Overview');
  useEffect(() => {
    if (activeGroup) setExpandedGroup(activeGroup);
  }, [activeGroup]);
  const exit = () => {
    signOut();
    void navigate('/sign-in', { replace: true });
  };
  return (
    <div className="flex min-h-dvh items-stretch bg-secondary">
      <a
        className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50 focus:rounded-lg focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-primary"
        href="#main-content"
      >
        Skip to content
      </a>
      <aside className="sticky top-0 flex h-dvh w-64 shrink-0 flex-col border-r border-secondary bg-primary">
        <div className="flex h-16 shrink-0 items-center gap-2.5 border-b border-secondary px-5">
          <img src={glyph} alt="" className="size-7 shrink-0" />
          <img src={wordmark} alt="Closet Theory" className="h-4 w-auto" />
        </div>
        {/* prettier-ignore */}
        <nav aria-label="Admin primary navigation"
          className="flex min-h-0 flex-1 flex-col gap-5 overflow-x-hidden overflow-y-auto px-3 py-4"
        >
          {visibleGroups.map((group) => (
            <NavigationGroup
              key={group.label}
              group={group}
              currentPath={location.pathname}
              expanded={expandedGroup === group.label}
              onToggle={() =>
                setExpandedGroup((current) => (current === group.label ? '' : group.label))
              }
            />
          ))}
        </nav>
        <footer className="flex shrink-0 items-center justify-between gap-2 border-t border-secondary px-5 py-3 text-xs text-tertiary">
          <span className="flex items-center gap-2">
            <span className="size-2 rounded-full bg-success-solid" />
            {import.meta.env.MODE}
          </span>
          <Button size="sm" color="tertiary" onClick={exit}>
            Sign out
          </Button>
        </footer>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col" aria-live="polite">
        <Outlet />
      </div>
    </div>
  );
}

function NavigationGroup({
  group,
  currentPath,
  expanded,
  onToggle,
}: {
  group: {
    label: string;
    routes: readonly { to: string; label: string; end?: boolean; capability: AdminCapability }[];
  };
  currentPath: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const containsActive = group.routes.some((route) =>
    route.to === '/' ? currentPath === '/' : currentPath.startsWith(route.to),
  );
  const regionId = `nav-${group.label.toLowerCase().replaceAll(' ', '-')}`;
  return (
    <section className="flex flex-col gap-1">
      <button
        className={cx(
          'flex items-center justify-between gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold uppercase outline-brand focus-visible:outline-2',
          containsActive ? 'text-secondary' : 'text-quaternary hover:text-tertiary',
        )}
        type="button"
        aria-expanded={expanded}
        aria-controls={regionId}
        onClick={onToggle}
      >
        <span>{group.label}</span>
        <ChevronDown
          aria-hidden="true"
          className={cx('size-4 shrink-0 transition-transform', expanded ? '' : '-rotate-90')}
        />
      </button>
      <div id={regionId} hidden={!expanded} className="flex flex-col gap-0.5">
        {group.routes.map((route) => (
          <NavLink
            key={route.to}
            to={route.to}
            end={'end' in route ? route.end : false}
            className={({ isActive }) =>
              cx(
                'rounded-lg px-3 py-2 text-sm font-medium outline-brand focus-visible:outline-2',
                isActive
                  ? 'active bg-active text-primary'
                  : 'text-tertiary hover:bg-primary_hover hover:text-secondary',
              )
            }
          >
            {route.label}
          </NavLink>
        ))}
      </div>
    </section>
  );
}
